import { useEffect, type ReactNode } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { CloseIcon } from '@/design-system/icons'

interface Props {
  open: boolean
  onClose: () => void
  title?: string
  children: ReactNode
  action?: ReactNode
}

export function BottomSheet({ open, onClose, title, children, action }: Props) {
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, onClose])

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-40 bg-black/60"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.16 }}
            onClick={onClose}
          />
          <motion.div
            className="fixed inset-x-0 bottom-0 z-50 max-h-[92svh] overflow-hidden rounded-t-r-xl bg-bg-raised shadow-s4 flex flex-col"
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 320 }}
          >
            <div className="pt-2 flex justify-center">
              <div className="h-1 w-10 rounded-full bg-white/20" />
            </div>
            {title && (
              <div className="flex items-center justify-between px-5 pt-3 pb-1">
                <div className="text-title font-display font-semibold">{title}</div>
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full text-text-secondary hover:bg-white/5"
                  aria-label="Close"
                >
                  <CloseIcon />
                </button>
              </div>
            )}
            <div className="flex-1 overflow-y-auto px-5 pb-4">{children}</div>
            {action && (
              <div
                className="border-t border-divider-subtle px-5 py-3"
                style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}
              >
                {action}
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
